import React from 'react';
import { useInView } from 'react-intersection-observer';
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

// Animation keyframes
const fadeInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const EducationWrapper = styled.section`
  position: relative;
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 0;
`;

const SectionHeading = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: #f59e0b;
  margin-bottom: 3rem;
  position: relative;
  letter-spacing: 1px;
  
  &::after {
    content: '';
    width: 60px;
    height: 4px;
    background-color: #f59e0b;
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
`;

const Timeline = styled.div`
  position: absolute;
  left: 50%;
  top: 150px;
  bottom: 0;
  width: 4px;
  background: linear-gradient(to bottom, #f59e0b, #fbbf24, #f59e0b);
  transform: translateX(-50%);
  border-radius: 4px;
  
  @media (max-width: 768px) {
    left: 20px;
    transform: none;
  }
`;

const EducationContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: ${({ alignLeft }) => (alignLeft ? 'flex-start' : 'flex-end')};
  padding: 0 60px;
  margin-bottom: 5rem;
  opacity: 0;
  transform: translateX(${({ alignLeft }) => (alignLeft ? '-30px' : '30px')});
  animation: ${({ inView, alignLeft }) => inView && (alignLeft ? fadeInLeft : fadeInRight)} 0.6s ease-out forwards;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
    padding-left: 50px;
    margin-left: 20px;
    margin-bottom: 3rem;
    animation: ${({ inView }) => inView && fadeInUp} 0.6s ease-out forwards;
    transform: translateY(30px);
  }
`;

const TimeNode = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  background-color: #f59e0b;
  border-radius: 50%;
  z-index: 2;
  box-shadow: 0 0 0 6px rgba(245, 158, 11, 0.2);
  transition: all 0.3s ease;
  
  &:before {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    background-color: white;
    border-radius: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  
  @media (max-width: 768px) {
    left: 0;
    transform: translateX(-50%);
  }
`;

const EducationCard = styled.div`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  padding: 2rem;
  width: 45%;
  text-align: left;
  border: 1px solid rgba(245, 158, 11, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 1rem;
  }
`;

const Title = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #111827;
`;

const Institution = styled.div`
  color: #f59e0b;
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
  }
`;

const Date = styled.div`
  color: #6b7280;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
  }
`;

const Description = styled.p`
  color: #4b5563;
  line-height: 1.6;
  margin-top: 1rem;
  font-size: 0.95rem;
`;

const Education = () => {
  const { ref: ref1, inView: inView1 } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  const { ref: ref2, inView: inView2 } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  const { ref: ref3, inView: inView3 } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  return (
    <EducationWrapper>
      <SectionHeading>Education</SectionHeading>
      <Timeline />
      
      {/* Master of Computer Application */}
      <EducationContainer alignLeft ref={ref1} inView={inView1}>
        <TimeNode />
        <EducationCard>
          <Title>Master of Computer Application</Title>
          <Institution>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3L1 9L5 11.18V17.18L12 21L19 17.18V11.18L21 10.09V17H23V9L12 3Z" fill="#f59e0b"/>
            </svg>
            KIIT University
          </Institution>
          <Date>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM16.2 16.2L11 13V7H12.5V12.2L17 14.9L16.2 16.2Z" fill="#6b7280"/>
            </svg>
            Currently pursuing
          </Date>
          <Description>
            Focusing on advanced computer science concepts, software development methodologies, and emerging technologies to build a strong foundation for a career in tech.
          </Description>
        </EducationCard>
      </EducationContainer>
      
      {/* Bachelor of Science */}
      <EducationContainer ref={ref2} inView={inView2}>
        <TimeNode />
        <EducationCard>
          <Title>Bachelor of Science</Title>
          <Institution>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3L1 9L5 11.18V17.18L12 21L19 17.18V11.18L21 10.09V17H23V9L12 3Z" fill="#f59e0b"/>
            </svg>
            Fakir Mohan University, Balasore
          </Institution>
          <Date>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM16.2 16.2L11 13V7H12.5V12.2L17 14.9L16.2 16.2Z" fill="#6b7280"/>
            </svg>
            2018 - 2021
          </Date>
          <Description>
            Developed a strong foundation in scientific principles and analytical thinking, with coursework that emphasized problem-solving and critical reasoning skills.
          </Description>
        </EducationCard>
      </EducationContainer>
      
      {/* Higher Secondary Education */}
      <EducationContainer alignLeft ref={ref3} inView={inView3}>
        <TimeNode />
        <EducationCard>
          <Title>Higher Secondary Education</Title>
          <Institution>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 3L1 9L5 11.18V17.18L12 21L19 17.18V11.18L21 10.09V17H23V9L12 3Z" fill="#f59e0b"/>
            </svg>
            Upendranath College, Soro
          </Institution>
          <Date>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2ZM16.2 16.2L11 13V7H12.5V12.2L17 14.9L16.2 16.2Z" fill="#6b7280"/>
            </svg>
            2016 - 2018
          </Date>
          <Description> 
            Completed studies under the Council of Higher Secondary Education, Odisha with focus on core academic subjects that built a strong foundation for further education.
          </Description>
        </EducationCard>
      </EducationContainer>
    </EducationWrapper>
  );
};

export default Education;